<template>
  <div class="pt-2">
    <!-- Floating Action Bar for bulk actions -->
    <FloatingActionBar
      :selected="selected"
      :totalItems="totalItems"
      :showDeleteSelected="true"
      :show-assign="false"
      :showDeleteAll="false"
      :showUpdateStatus="false"
      :showSelectAll="false"
      @delete-selected="handleShowDialogDelete"
    />
    <!-- Stores Table -->
    <div class="overflow-x-auto overflow-y-auto">
      <v-data-table
        :headers="headers"
        :items="stores"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [5, 10, 15, 30] }"
        :server-items-length="totalItems"
        fixed-header
        class="table-radius border border-[#e0e0e0]"
        :loading="loadTable"
        v-model="selected"
        show-select
        item-key="_id"
      >
        <template v-for="(header, index) in headers" v-slot:[`header.${header.value}`]>
          <p
            :key="index"
            class="font-title"
            v-if="header.text?.length > 15"
            v-tooltip="{ text: header.text }"
          >
            {{ header.text }}
          </p>
          <p :key="index" v-else class="font-title">{{ header.text }}</p>
        </template>
        <template v-slot:item.storeName="{ item }">
          <td class="px-4 py-3 table-font">{{ item.storeName }}</td>
        </template>
        <template v-slot:item.storeDescription="{ item }">
          <td class="px-4 table-font">{{ item.storeDescription }}</td>
        </template>
        <template v-slot:item.storeLocation="{ item }">
          <td class="px-4 table-font">
            {{ item.storeLocation ? Object.values(item.storeLocation).join(', ') : '' }}
          </td>
        </template>
        <template v-slot:item.storeStatus="{ item }">
          <td
            :class="getStoreStatusClr(item.storeStatus)"
            class="px-4 table-font font-weight-medium"
          >
            {{ convertToTitleCase(item.storeStatus) }}
          </td>
        </template>
        <template v-slot:item.actions="{ item }">
          <td class="pl-4">
            <ActionButton
              :item="item"
              :canEdit="canEdit"
              :canDelete="canDelete"
              :handleEdit="() => editStore(item)"
              :handleDelete="() => confirmDeleteStore(item)"
              :showEditButton="canEdit"
              :showDeleteButton="canDelete"
            />
          </td>
        </template>
      </v-data-table>
    </div>

    <!-- Delete Confirmation Dialog -->
    <AlertPopUp
      :show-confirmation-dialog.sync="showDeleteConfirmation"
      :confirmation-title="'Confirm Delete'"
      :confirmation-message="
        storeToDelete && storeToDelete._id
          ? `Are you sure you want to delete the store '${storeToDelete?.storeName}'?`
          : `Are you sure you want to delete ${selected.length} selected stores?`
      "
      :confirmation-action-text="'Delete'"
      :confirmation-action-color="'error'"
      :perform-action="storeToDelete && storeToDelete._id ? performDeleteStore : performBulkDelete"
      :cancel="cancelDelete"
      :loading="deleteLoading"
    />
  </div>
</template>

<script>
import ActionButton from '@/components/ActionButton.vue'
import AlertPopUp from '@/components/AlertPopUp.vue'
import { debounce, hasPermission, convertToTitleCase } from '@/utils/common'
import FloatingActionBar from '@/components/Workforce/CollectionManagement/CaseModules/FloatingActionBar.vue'
import permissionData from '@/utils/permissions'
import { axiosUrl } from '@/utils/workforceApi'

export default {
  name: 'InventoryIn',
  components: {
    ActionButton,
    FloatingActionBar,
    AlertPopUp
  },
  props: {
    filterStore: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      userData: this.$store.state.wfmPermissions,
      stores: [],
      options: { itemsPerPage: 5, page: 1 },
      totalItems: 0,
      loadTable: true,
      // Delete confirmation dialog
      showDeleteConfirmation: false,
      storeToDelete: null,
      deleteLoading: false,
      // For FloatingActionBar
      selected: [],
      showDeleteDialog: false
    }
  },
  watch: {
    options: {
      handler() {
        this.fetchStores()
      },
      deep: true
    },
    filterStore: {
      handler(newVal, oldVal) {
        // Reset to first page when search changes
        this.options.page = 1
        // If search is cleared, fetch immediately
        if (!newVal) {
          this.fetchStores()
        } else {
          // Use debounce for typing but with shorter delay
          this.handleInput()
        }
      }
    }
  },
  computed: {
    headers() {
      return [
        { text: 'Store Name', value: 'storeName', sortable: false },
        { text: 'Description', value: 'storeDescription', sortable: false },
        { text: 'Location', value: 'storeLocation', sortable: false },
        { text: 'Store Status', value: 'storeStatus', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false }
      ]
    },
    canEdit() {
      return hasPermission(this.userData, permissionData.inventoryWrite)
    },
    canDelete() {
      return hasPermission(this.userData, permissionData.inventoryWrite)
    }
  },
  methods: {
    getStoreStatusClr(status) {
      const statuses = {
        OPERATIONAL: 'text-green-500',
        NON_OPERATIONAL: 'text-red-500'
      }
      return statuses[status]
    },
    async fetchStores() {
      try {
        const { page, itemsPerPage } = this.options
        const params = { page, limit: itemsPerPage }
        if (this.filterStore) {
          params.search = this.filterStore
        }
        this.loadTable = true
        const response = await this.$axios.get(axiosUrl.stores.getStores, {
          params
        })
        this.loadTable = false
        this.stores = response.data.stores
        this.totalItems = response.data.pagination?.totalCount
      } catch (error) {
        console.error('An error occurred:', error)
        this.loadTable = false
      }
    },
    handleInput: debounce(function () {
      this.fetchStores()
      this.options.page = 1
    }, 1000),
    editStore(item) {
      this.$emit('edit-store', item)
    },
    confirmDeleteStore(item) {
      this.storeToDelete = item
      this.showDeleteConfirmation = true
    },
    cancelDelete() {
      this.showDeleteConfirmation = false
      this.storeToDelete = null
      this.deleteLoading = false
    },
    convertToTitleCase(item) {
      return convertToTitleCase(item)
    },
    async performDeleteStore() {
      if (!this.storeToDelete) return

      this.deleteLoading = true
      try {
        const response = await this.$axios.delete(
          `${axiosUrl.stores.deleteStores}/${this.storeToDelete._id}`
        )
        console.log('Store deleted successfully', response.data)
        this.$toast.success('Store deleted successfully')
        this.cancelDelete()
        this.fetchStores() // Refresh the table
      } catch (error) {
        console.error('Error deleting store:', error)
        this.$toast.error(
          'Error deleting store: ' + (error.response?.data?.message || error.message)
        )
      } finally {
        this.deleteLoading = false
      }
    },
    async performBulkDelete() {
      if (this.selected.length === 0) return

      this.deleteLoading = true
      try {
        // Delete all selected stores
        const deletePromises = this.selected.map((item) =>
          this.$axios.delete(`${axiosUrl.stores.deleteStores}/${item._id}`)
        )

        await Promise.all(deletePromises)

        this.$toast.success('Selected stores deleted successfully')
        this.cancelDelete()
        this.selected = [] // Clear selection
        this.fetchStores() // Refresh the table
      } catch (error) {
        console.error('Error deleting selected stores:', error)
        this.$toast.error(
          'Error deleting selected stores: ' + (error.response?.data?.message || error.message)
        )
      } finally {
        this.deleteLoading = false
      }
    },
    toggleshowAddStore() {
      this.$emit('show-add-store')
    },
    handleShowDialogDelete() {
      // For bulk delete via floating action bar, we show the confirmation dialog
      this.storeToDelete = { storeName: `${this.selected.length} selected stores` }
      this.showDeleteConfirmation = true
    }
  },
  mounted() {
    this.fetchStores()
  }
}
</script>

<style scoped>
.v-data-table >>> .v-data-table__wrapper > table > tbody > tr > td > .v-data-table__checkbox {
  text-align: center;
}
.table-radius {
  border-radius: 0px !important;
}
.font-title {
  font-size: 0.875rem;
  color: #000;
  margin-bottom: 0px !important;
  max-width: 120px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
/* .table-font {
  font-size: 0.8rem !important;
} */
</style>
